import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Target,
  TrendingDown,
  Lightbulb,
  MessageCircle,
  Eye,
  FileText,
  Copy,
  Check,
  Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { PILLARS } from '../lib/pillars';
import { calculateProfileScores } from '../lib/calculations';
import { runFullAnalysis } from '../lib/analysis-engine';
import { HAEVNSummary } from './HAEVNSummary';
import { ExperienceMap } from './ExperienceMap';

// ============================================
// DRIVER BAR
// ============================================
const DriverBar = ({ name, importance }) => {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <span className="text-sm font-light text-foreground">{name}</span>
        <span className="text-xs text-muted-foreground">{importance}/10</span>
      </div>
      <div className="relative h-2 bg-secondary rounded-full overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-[hsl(var(--gold))] rounded-full transition-all"
          style={{ width: `${(importance / 10) * 100}%` }}
        />
      </div>
    </div>
  );
};

// ============================================
// PUT IT INTO WORDS (inline)
// ============================================
const PutItIntoWordsBlock = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  if (!text) return null;

  return (
    <Card className="border-border bg-card" data-testid="section-put-it-into-words">
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[hsl(var(--gold))]" />
          <CardTitle className="text-xl font-light">Put It Into Words</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground text-sm mb-4">
          A summary for personal reflection, journaling, or clarity:
        </p>
        <div className="p-4 bg-secondary/30 rounded-xl border border-border">
          <p className="text-foreground leading-relaxed">{text}</p>
        </div>
        <Button
          onClick={handleCopy}
          variant="ghost"
          size="sm"
          className="mt-3 text-muted-foreground"
          data-testid="copy-summary-btn"
        >
          {copied ? (
            <><Check className="w-3 h-3 mr-2" />Copied</>
          ) : (
            <><Copy className="w-3 h-3 mr-2" />Copy text</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// ============================================
// MAIN PROFILE RESULT CARD
// ============================================
export const ProfileResultCard = ({ profile }) => {
  const scores = useMemo(() => calculateProfileScores(profile), [profile]);
  const analysis = useMemo(() => runFullAnalysis(profile, scores), [profile, scores]);

  if (!scores || !analysis) return null;

  const {
    intentAnchor,
    whatHitsHardest,
    whatsNotComingThrough,
    whatThisTellsYou,
    experienceConfidenceLabel,
    realLifeTranslation,
    contextLens,
    putItIntoWords,
    haevnSummary,
    reflectionPrompts
  } = analysis;

  return (
    <div className="space-y-16" data-testid="profile-result-card">

      {/* ═══════════════════════════════════════════ */}
      {/* GROUP 1: UNDERSTANDING YOUR EXPERIENCE      */}
      {/* ═══════════════════════════════════════════ */}
      <section data-testid="group-understanding">
        <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground/70 mb-6">
          Understanding Your Experience
        </h2>
        <div className="space-y-6">
          {/* Intent Anchor */}
          <Card className="border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.05)]" data-testid="section-intent-anchor">
            <CardContent className="py-6">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-[hsl(var(--gold))] mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                    Intent
                  </h3>
                  <p className="text-foreground leading-relaxed font-light">
                    {intentAnchor}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Experience Map */}
          <ExperienceMap profile={profile} scores={scores} />

          {/* What Matters Most */}
          <Card className="border-border bg-card" data-testid="section-drivers-chart">
            <CardHeader>
              <CardTitle className="text-xl font-light">What Matters Most</CardTitle>
              {(() => {
                const primary = PILLARS
                  .filter(p => (profile.drivers?.[p.id]?.importance || 0) >= 7)
                  .map(p => p.shortName);
                return primary.length > 0 ? (
                  <p className="text-sm text-muted-foreground mt-1">
                    Primary Drivers: {primary.join(', ')}
                  </p>
                ) : null;
              })()}
            </CardHeader>
            <CardContent className="space-y-4">
              {PILLARS.map(pillar => (
                <DriverBar
                  key={pillar.id}
                  name={pillar.shortName}
                  importance={profile.drivers?.[pillar.id]?.importance || 0}
                />
              ))}

              {/* How Clear This Is For You — seeking mode */}
              {profile.mode === 'seeking' && experienceConfidenceLabel && (
                <div className="pt-6 mt-4 border-t border-border/50" data-testid="section-experience-confidence">
                  <h4 className="text-base font-medium text-foreground mb-2">How Clear This Is For You</h4>
                  <p className="text-sm text-foreground/80 leading-relaxed">{experienceConfidenceLabel}</p>
                </div>
              )}



            </CardContent>
          </Card>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* GROUP 2: WHERE THINGS ALIGN                 */}
      {/* ═══════════════════════════════════════════ */}
      <section data-testid="group-experience-shape">
        <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground/70 mb-6">
          {profile.mode === 'seeking' ? 'What This Experience Needs' : 'What Shapes This Experience'}
        </h2>
        <div className="space-y-6">
          {/* What Hits Hardest */}
          <Card className="border-border bg-card" data-testid="section-what-hits-hardest">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[hsl(var(--teal))]" />
                <CardTitle className="text-xl font-light">What Hits Hardest</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm mb-6">{whatHitsHardest.description}</p>
              <div className="space-y-6">
                {whatHitsHardest.drivers.map(driver => (
                  <div key={driver.pillarId} className="space-y-2">
                    <h4 className="text-base font-medium text-foreground">
                      {driver.name}
                      <span className="ml-2 text-xs text-muted-foreground font-normal">{driver.importance}/10</span>
                    </h4>
                    <div className="pl-3 border-l-2 border-[hsl(var(--teal)/0.4)] space-y-1">
                      <span className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--teal))]">
                        When present
                      </span>
                      <p className="text-sm text-foreground/80">{driver.whenPresent}</p>
                    </div>
                    <div className="pl-3 border-l-2 border-primary/40 space-y-1">
                      <span className="text-xs font-medium uppercase tracking-wider text-primary">
                        When it is not
                      </span>
                      <p className="text-sm text-foreground/80">
                        {driver.whenNot} — even if everything else looks right.
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* What's Not Coming Through (current mode only) */}
          {whatsNotComingThrough && (
            <Card className="border-border bg-card" data-testid="section-whats-not-coming-through">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-[hsl(var(--gold))]" />
                  <CardTitle className="text-xl font-light">What Falls Flat Without It</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">{whatsNotComingThrough.text}</p>
                <div className="space-y-2">
                  {whatsNotComingThrough.items.map(item => (
                    <div key={item.pillarId} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{item.name}</span>
                      <span className="text-xs text-muted-foreground">
                        importance {item.importance}/10
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* GROUP 3: WHAT THIS TELLS YOU                */}
      {/* ═══════════════════════════════════════════ */}
      <section data-testid="group-tells-you">
        <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground/70 mb-6">
          What This Means
        </h2>
        <div className="space-y-6">
          {/* What This Tells You */}
          <Card className="border-border bg-card" data-testid="section-what-this-tells-you">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-[hsl(var(--teal))]" />
                <CardTitle className="text-xl font-light">What This Means</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-foreground leading-relaxed">{whatThisTellsYou}</p>
            </CardContent>
          </Card>

          {/* Real-Life Translation */}
          {realLifeTranslation.length > 0 && (
            <Card className="border-border bg-card" data-testid="section-real-life-translation">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[hsl(var(--gold))]" />
                  <CardTitle className="text-xl font-light">Real-Life Translation</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {realLifeTranslation.map((item) => (
                  <div key={item.pillarId} className="space-y-3">
                    <h4 className="text-base font-medium text-foreground">{item.name}</h4>
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--teal))]">
                        When present
                      </span>
                      <ul className="mt-1.5 space-y-1">
                        {item.whenPresent.map((line, i) => (
                          <li key={i} className="text-sm text-foreground/80 flex gap-2">
                            <span className="text-[hsl(var(--teal))] shrink-0">+</span> {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--gold))]">
                        When it is not there
                      </span>
                      <ul className="mt-1.5 space-y-1">
                        {item.whenMissing.map((line, i) => (
                          <li key={i} className="text-sm text-foreground/80 flex gap-2">
                            <span className="text-[hsl(var(--gold))] shrink-0">-</span> {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        What this points to
                      </span>
                      <ul className="mt-1.5 space-y-1">
                        {item.whatItPointsTo.map((line, i) => (
                          <li key={i} className="text-sm text-foreground/80 flex gap-2">
                            <span className="text-muted-foreground shrink-0">&#8594;</span> {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Context Lens */}
          <Card className="border-border bg-card" data-testid="section-context-lens">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-[hsl(var(--teal))]" />
                <CardTitle className="text-xl font-light">Context Lens</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--teal)/0.1)] text-[hsl(var(--teal))] font-medium">
                  {profile.mode === 'current' ? 'Current Reality' : 'Seeking'}
                </span>
              </div>
              <p className="text-foreground/90 leading-relaxed">{contextLens}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* GROUP 4: USING THIS                         */}
      {/* ═══════════════════════════════════════════ */}
      <section data-testid="group-using-this">
        <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground/70 mb-6">
          Using This
        </h2>
        <div className="space-y-6">
          {/* Put It Into Words */}
          <PutItIntoWordsBlock text={putItIntoWords} />

          {/* HAEVN Profile Summary */}
          <HAEVNSummary summary={haevnSummary} profileLabel={profile.label} />

          {/* Reflection Prompts */}
          <Card className="border-border bg-card" data-testid="section-reflection-prompts">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-[hsl(var(--teal))]" />
                <CardTitle className="text-xl font-light">Reflection Prompts</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                {reflectionPrompts.map((prompt, i) => (
                  <li key={i} className="flex items-start gap-3 text-muted-foreground">
                    <span className="text-[hsl(var(--gold))] font-light">{i + 1}.</span>
                    <span className="leading-relaxed">{prompt}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

    </div>
  );
};
